import { useMemo } from 'react';
import type { CSSProperties } from 'react';

import { cn, Skeleton } from '@/shared';
import { getLocale, t } from '@/i18n';
import { formatLocalizedDate, getPriceFractionDigits } from '@/shared/lib/utils/format';
import { pickLocalizedName } from '@/shared/lib/utils/display-name';
import { useGlobalIndicesQuery, useGlobalCommoditiesQuery } from '@/features/global-market';

import type { GlobalMarketPrice } from '@/entities/global-market/types';
import { getTickerCopyCount, sortMarketPricesBySymbol } from './ticker-utils';

import './global-market-ticker.css';

const formatPrice = (price: number) => {
  const digits = Math.max(getPriceFractionDigits(price), 2);
  return price.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

/**
 * 🔴 [TRAD-3437] `currency` 는 **원자재에만 오고 지수 응답엔 키 자체가 없다**(서버가 의도적으로
 * 뺀다 — `GlobalMarketPriceRes:48` + `@JsonInclude(NON_NULL)`). 종전 시그니처는 `string` 이었고
 * 부재 처리는 **우연히** 됐다(`undefined === 'USD'` 가 false 라 빈 문자열로 떨어짐). 우연을
 * 명시로 바꾼다 — 부재를 먼저 갈라 「모르는 통화는 접두사 없음」을 코드가 말하게 한다.
 * 🔴 미지 통화를 `$` 로 대체하지 않는다(모르는 것을 아는 상태로 바꿔치기 금지).
 */
const currencyPrefix = (currency: string | undefined) => {
  if (!currency) return '';
  if (currency === 'USD') return '$';
  return '';
};

/**
 * TRAD-2059: 서버가 `name`(영문)과 `nameKo`(한글)를 **둘 다** 내려주는데 `nameKo` 로 고정돼 있어
 * EN 로케일에서도 "다우존스"가 나왔다. 클라 i18n 키로 뽑으면 안 된다 — 종목 목록은 서버가 정하므로
 * 새 지수가 추가되면 카탈로그에 없어 빈칸이 된다. 서버가 준 필드를 로케일로 고르는 것이 맞다.
 * `name` 이 비어 있으면 `nameKo` 로 폴백한다(빈 라벨 방지).
 *
 * TRAD-1954: 실제 로케일 분기 로직은 `shared/lib/utils/display-name.ts` 의 `pickLocalizedName`
 * 으로 승격됐다(크립토 마켓 검색/목록/관심종목이 같은 결함으로 재사용). 이 함수는 `GlobalMarketPrice`
 * 필드명(`name`/`nameKo`) 전용 얇은 래퍼로 남긴다 — 호출부·기존 테스트를 그대로 유지하기 위함.
 */
export const pickDisplayName = (item: Pick<GlobalMarketPrice, 'name' | 'nameKo'>, locale: string): string =>
  pickLocalizedName(item.nameKo, item.name, locale);

/**
 * TRAD-2526 — 서버의 `"yyyy-MM-dd"` 를 뷰어 로케일 표기(KO «10월 2일» / EN «Oct 2»)로 바꾼다(순수).
 * 🔴 «달력 날짜 그대로» 다 — UTC 자정으로 만들고 UTC 로 찍어 뷰어 시간대가 날짜를 하루 밀지 못하게 한다
 * (`new Date('2026-10-02')` 를 로컬 TZ 로 찍으면 미주 시간대에서 10월 1일이 된다).
 * 형식이 어긋나거나 없는 날짜(`2026-02-30`)면 `null` — 호출부가 날짜 없는 문구로 폴백한다.
 */
export const formatTradingDate = (value: string | null | undefined): string | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return formatLocalizedDate(date, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/**
 * TRAD-2526 — 휴장 표기(순수). 3상태다 — bool 로 접지 않는다:
 *   `false`     → «휴장 · 직전 거래일 종가 기준» (등락률이 «오늘» 수치로 읽히지 않게 기준을 같이 말한다)
 *   `true`      → 표기 없음(평소 표시)
 *   `undefined` → 표기 없음. 판정 대상이 아닌 종목(해외지수·원자재)이거나 서버 미배포 — «개장»이라고도 말하지 않는다.
 * 날짜형(2026-10-04 backend 신규 필드 `lastTradingDate`): 휴장 + 날짜가 «읽히면» «휴장 · 10월 2일 종가»,
 * 날짜가 없거나(서버가 값·날짜 짝을 확인 못 함 / 필드 없는 구버전 응답) 형식이 어긋나면 종전 문구로 폴백한다.
 * 🔴 `fetchedAt` 은 거래일로 쓰지 않는다(휴장일 재시작 직후 그날 시각이 찍힌 실측 2026-09-13). 클라가 달력을
 * 계산하지도 않는다(정책 §3). 정본 문구: docs-ops/2026-09-15-planner-TRAD-2526-holiday-display-policy.md §2-1.
 */
export const getMarketClosedLabel = (item: Pick<GlobalMarketPrice, 'isMarketOpen' | 'lastTradingDate'>): string | null => {
  if (item.isMarketOpen !== false) return null;
  const date = formatTradingDate(item.lastTradingDate);
  return date
    ? t('market.status.closed_with_date', { date })
    : `${t('market.status.closed')} · ${t('market.status.as_of_prev_close')}`;
};

const TickerItem = ({ item }: { item: GlobalMarketPrice }) => {
  const closedLabel = getMarketClosedLabel(item);
  const isRise = item.changePercent > 0;
  const isFall = item.changePercent < 0;

  return (
    // 중립 글자색(text-gray-400)은 항목 컨테이너에 한 번만 둔다 — 종목명과 휴장 표기가 같은 색을 상속한다
    // (가격·등락률은 각자 색을 지정). 생 팔레트 래칫(TRAD-2116) 상한을 늘리지 않기 위해 색을 복제하지 않는다.
    <span className="inline-flex items-center gap-2 px-5 whitespace-nowrap text-gray-400">
      <span className="text-xs">{pickDisplayName(item, getLocale())}</span>
      <span className="text-gray-100 font-medium text-xs tabular-nums">
        {currencyPrefix(item.currency)}{formatPrice(item.price)}
      </span>
      <span
        className={cn(
          'font-semibold text-xs tabular-nums',
          isRise && 'text-price-rise neon-glow-rise',
          isFall && 'text-price-fall neon-glow-fall',
          !isRise && !isFall && 'text-gray-500',
        )}
      >
        {isRise ? '+' : ''}{item.changePercent.toFixed(2)}%
      </span>
      {/* 중립색(종목명과 같은 색을 상속) — 상승/하락 색을 쓰면 «상태»가 «방향»으로 읽힌다(정책 §2-3). */}
      {closedLabel && (
        <span className="text-xs" data-testid="market-closed-label">
          {closedLabel}
        </span>
      )}
    </span>
  );
};

// TRAD-2086: 지수/원자재 두 쿼리 중 하나만 먼저 도착한 상태로 렌더하면, 이후 나머지가 도착할 때
// 트랙 폭이 바뀌어 퍼센트 기반 transform(-100%/N) 애니메이션이 튄다. 또한 기존 `return null`은
// 로딩 중 바가 아예 없다가 갑자기 나타나 레이아웃이 시프트되는 문제가 있었다. 최종 바와 높이가
// 같은 자리표시자를 렌더해 두 문제를 모두 막는다.
const TickerSkeleton = () => (
  <div className="w-full overflow-hidden bg-gray-900 border-b border-gray-800 py-2.5" aria-hidden="true">
    <div className="flex items-center gap-5 px-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-3 w-16 bg-gray-700" />
      ))}
    </div>
  </div>
);

export const GlobalMarketTicker = () => {
  const { data: indicesData, isPending: isIndicesPending } = useGlobalIndicesQuery();
  const { data: commoditiesData, isPending: isCommoditiesPending } = useGlobalCommoditiesQuery();

  // 최초 로드 중(둘 중 하나라도 아직 해석 전)에만 true. 이후 5분 주기 refetch는 isPending에
  // 영향을 주지 않으므로, 마운트 이후 항목 수는 애니메이션 재생 중 바뀌지 않는다.
  const isInitialLoading = isIndicesPending || isCommoditiesPending;

  const items = useMemo(() => {
    if (isInitialLoading) return [];
    // 카테고리 그룹(지수→원자재)은 유지, 그룹 내부는 symbol 고정 정렬 — 순서 흔들림 방지
    return [
      ...sortMarketPricesBySymbol(indicesData?.indices ?? []),
      ...sortMarketPricesBySymbol(commoditiesData?.commodities ?? []),
    ];
  }, [isInitialLoading, indicesData, commoditiesData]);

  if (isInitialLoading) return <TickerSkeleton />;
  if (items.length === 0) return null;

  const copies = getTickerCopyCount(items.length);

  return (
    <div className="w-full overflow-hidden bg-gray-900 border-b border-gray-800 py-2.5">
      <div className="ticker-track" style={{ '--ticker-copies': copies } as CSSProperties}>
        {Array.from({ length: copies }).map((_, copyIndex) => (
          <div className="ticker-content" key={copyIndex} aria-hidden={copyIndex !== 0}>
            {items.map((item) => (
              <TickerItem key={`${copyIndex}-${item.symbol}`} item={item} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
